import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import qs from "qs";

export function useFilterParams(defaultFilters) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFiltersRef = useRef(defaultFilters);

  // Write missing non-empty defaults into the URL on first mount
  useEffect(() => {
    const parsed = qs.parse(searchParams.toString(), { arrayFormat: "repeat" });
    const missing = Object.fromEntries(
      Object.entries(defaultFiltersRef.current).filter(([key, value]) => {
        if (key in parsed) return false;
        if (value === "" || value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );
    if (Object.keys(missing).length > 0) {
      setSearchParams(qs.stringify({ ...parsed, ...missing }, { arrayFormat: "repeat" }), { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useMemo(() => {
    const parsed = qs.parse(searchParams.toString(), { arrayFormat: "repeat" });
    return Object.fromEntries(
      Object.entries(defaultFiltersRef.current).map(([key, defaultValue]) => {
        const value = parsed[key];
        if (value === undefined) return [key, defaultValue];
        if (Array.isArray(defaultValue)) return [key, value ? [value].flat() : []];
        if (typeof defaultValue === "number") return [key, Number(value) || defaultValue];
        return [key, value];
      }),
    );
  }, [searchParams]);

  const updateFilters = useCallback(
    (newFilters, resetPage = true) => {
      setSearchParams((prev) => {
        const merged = {
          ...qs.parse(prev.toString(), { arrayFormat: "repeat" }),
          ...newFilters,
          ...(resetPage && !newFilters.page ? { page: 1 } : {}),
        };
        const cleaned = Object.fromEntries(
          Object.entries(merged).filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)),
        );
        return qs.stringify(cleaned, { arrayFormat: "repeat" });
      });
    },
    [setSearchParams],
  );

  return { filters, updateFilters };
}